import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Modal from 'chameleon/ui-stack/ui/base/modal';
import Button from 'chameleon/ui-stack/ui/base/button';
import Icon from 'chameleon/ui-stack/ui/base/icon';

const WarningModal = (props) => {
  const {
    children,
    title,
    dialogClassName,
    wrapperClassName,
    headingClassName,
    contentClassName,
    onSuccess,
    contentLabel,
    aria,
    size,
    content,
    isScrollableContent,
    hasTitleWithTooltip,
    onClose,
    onSecondaryBtnClick,
    warning,
  } = props;
  const {
    btnPrimary,
    btnPrimaryType = 'primary',
    btnPrimaryAria = {},
    btnSecondaryType = 'secondary',
    btnSecondary,
  } = content || {};

  const isPrimaryBtn = btnPrimaryType === 'primary';
  const isPrimaryDangerBtn = btnPrimaryType === 'danger';
  const isPrimarySuccessBtn = btnPrimaryType === 'success';
  const isSecondaryBtn = btnSecondaryType === 'secondary';
  const isSecondaryDangerBtn = btnSecondaryType === 'danger';

  const _onClose = (event) => {
    onClose(event);
  };

  return createPortal(
    <Modal
      size={size}
      title={title}
      dialogClassName={dialogClassName}
      wrapperClassName={wrapperClassName}
      headingClassName={headingClassName}
      contentClassName={contentClassName}
      contentLabel={contentLabel}
      aria={aria}
      isScrollableContent={isScrollableContent}
      hasTitleWithTooltip={hasTitleWithTooltip}
      onClose={_onClose}
      contentWithSpacing={false}
      bottomContent={
        <div className="footer flex gap-x-4 justify-end mt-auto p-6 border-t border-gray-300">
          {btnSecondary && (
            <Button
              isNaked
              isSecondary={isSecondaryBtn}
              isDanger={isSecondaryDangerBtn}
              accessibility={{
                'data-test-id': 'button-secondary',
                ...btnPrimaryAria,
              }}
              onClick={onSecondaryBtnClick || onClose}
            >
              {btnSecondary}
            </Button>
          )}

          {btnPrimary && (
            <Button
              isPrimary={isPrimaryBtn}
              isSuccess={isPrimarySuccessBtn}
              isDanger={isPrimaryDangerBtn}
              accessibility={{
                'data-test-id': 'button-primary',
              }}
              onClick={onSuccess}
            >
              {btnPrimary}
            </Button>
          )}
        </div>
      }
    >
      {warning ? (
        <div className="px-6 py-4 bg-yellow-100 text-gray-900 text-14 flex gap-x-2.5">
          <Icon name="warning_triangle" className="" width={18} height={18} />
          {warning}
        </div>
      ) : null}
      <div className="px-6 text-16">{children}</div>
    </Modal>,
    document.querySelector('#modal'),
  );
};

WarningModal.propTypes = {
  // The title of the modal
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The class to style the dialog window.
  dialogClassName: PropTypes.string,
  // The class to style the modal content.
  contentClassName: PropTypes.string,
  // The class to style the modal wrapper.
  wrapperClassName: PropTypes.string,
  // The class to style the modal heading title.
  headingClassName: PropTypes.string,
  // Callback for submitting when confirmation buttons are available.
  onSuccess: PropTypes.func,
  // Callback for closing the modal and X button
  onClose: PropTypes.func,
  // Callback for clicking the secondary button
  onSecondaryBtnClick: PropTypes.func,
  // The accessibility content label
  contentLabel: PropTypes.func,
  // Object for setting accessibility aria options
  aria: PropTypes.object,
  // Sets the size of the modal
  size: PropTypes.string,
  // Sets the modal buttons options
  content: PropTypes.object,
  // If true it removes the scrolls from modal body
  isScrollableContent: PropTypes.bool,
  // If true the title tooltip algorithm is used to determine
  // if title needs tooltip
  hasTitleWithTooltip: PropTypes.bool,
  // Warning content
  warning: PropTypes.any,
};

WarningModal.defaultProps = {
  title: <div />,
  dialogClassName: null,
  contentClassName: null,
  wrapperClassName: null,
  headingClassName: null,
  showCloseButton: true,
  onSuccess: () => {},
  onClose: () => {},
  onSecondaryBtnClick: null,
  contentLabel: '',
  aria: {},
  size: 'medium',
  content: {},
  isScrollableContent: false,
  hasTitleWithTooltip: true,
  warning: null,
};

export default WarningModal;
